import { useApolloClient } from '@apollo/client'
import {
  ApprovalMaterializedApprovalFragment,
  FetchEditTimesheetRequestDocument,
  MaterializedApprovalFlowPolicyFragment,
  type FetchEditTimesheetRequestQuery,
} from 'generated/resources/graphql'
import moment from 'moment'

import type { RequestComponentProps } from 'modules/notifications/pages/RequestsPage/RequestItems/RequestItem/base'

import BasePendingApproval from '../BasePendingApprovals'
import { findValueInBlock } from '../FindInEmailBlock'
import LeftBlock from './components/LeftBlock'
import RightBlock from './components/RightBlock'
import { CollapseContentData, EmailBlockData } from './types'

type AttendanceEditTimesheetRequest =
  NonNullable<FetchEditTimesheetRequestQuery['attendance']['editTimesheetRequest']>

const AttendanceApprovals = ({
  request,
  index,
  compact,
  removeRequestById,
}: RequestComponentProps) => {
  const graphql = useApolloClient()

  /**
   * Get the EditTimesheetRequest
   */
  const getEditTimesheetRequest = async (id: number): Promise<AttendanceEditTimesheetRequest> => {
    const result = await graphql.query({
      query: FetchEditTimesheetRequestDocument,
      variables: { id },
    })

    if (!result.data.attendance.editTimesheetRequest) {
      throw new Error('EditTimesheetRequest not found')
    }

    return result.data.attendance.editTimesheetRequest
  }

  /*
   * Object with the data of the email blocks
   */
  const getEmailBlockResources = (flow: ApprovalMaterializedApprovalFragment): EmailBlockData => {
    return {
      ownerName: findValueInBlock(flow, 'owner-name'),
    }
  }

  const getTimeFromISODate = (isoDate: string): string => {
    if (!isoDate) return ''
    return moment.utc(isoDate).format('HH:mm')
  }

  /**
   * Fetch all the necessary resources and return them
   *
   */
  const fetchResources = async (
    flow: ApprovalMaterializedApprovalFragment,
    policy?: MaterializedApprovalFlowPolicyFragment | null
  ) => {
    if (!policy?.seeAttendance?.can) return { noPermission: !policy?.seeAttendance?.can }

    const blockData = getEmailBlockResources(flow)

    const editTimesheetRequest = await getEditTimesheetRequest(flow.resourceId)
    const shift = editTimesheetRequest.attendanceShift
    const {
      name: breakName,
      paid: breakPaid,
      id: breakId,
    } = shift?.timeSettingsBreakConfiguration || {}

    const originalClockIn = shift?.clockIn ? getTimeFromISODate(shift.clockIn) : ''
    const originalClockOut = shift?.clockOut ? getTimeFromISODate(shift.clockOut) : ''
    const originalObservations = shift?.observations || ''
    const originalLocationType = shift?.locationType || ''
    const originalIsBreak = shift?.workable === false
    const originalBreakName = breakName || ''
    const originalBreakPaid = !!breakPaid
    const originalBreakId = breakId || undefined
    const referenceDate = shift?.referenceDate || editTimesheetRequest.referenceDate || ''

    const newClockIn = editTimesheetRequest.clockIn ? editTimesheetRequest.clockIn : ''
    const newClockOut = editTimesheetRequest.clockOut ? editTimesheetRequest.clockOut : ''
    const newObservations = editTimesheetRequest.observations || ''
    const newLocationType = editTimesheetRequest.locationType || ''
    const newIsBreak = !editTimesheetRequest.workable
    const newBreakName = editTimesheetRequest.timeSettingsBreakConfiguration?.name
    const newBreakPaid = editTimesheetRequest.timeSettingsBreakConfiguration?.paid
    const newBreakId = editTimesheetRequest.timeSettingsBreakConfiguration?.id

    const date = shift?.date ?? editTimesheetRequest.date ?? ''
    const [year, month] = date.split('-')

    return {
      approvalFlow: flow,
      date,
      referenceDate,
      newClockIn,
      newClockOut,
      newObservations,
      newLocationType,
      originalClockIn,
      originalClockOut,
      originalObservations,
      originalLocationType,
      year: Number(year),
      month: Number(month),
      employeeId: editTimesheetRequest.employeeId,
      requestType: editTimesheetRequest.requestType,
      originalIsBreak,
      originalBreakName,
      originalBreakPaid,
      originalBreakId,
      newIsBreak,
      newBreakName,
      newBreakPaid,
      newBreakId,
      ...blockData,
    }
  }

  return (
    <BasePendingApproval
      request={request}
      index={index}
      compact={compact}
      getApprovalData={(flow, policies) => fetchResources(flow, policies)}
      removeRequestById={removeRequestById}
      collapseContentBlocks={(data) => ({
        rightBlock: <RightBlock collapseContentData={data as CollapseContentData} />,
        leftBlock: <LeftBlock collapseContentData={data as CollapseContentData} />,
      })}
      icon="edit-file-filled"
    />
  )
}

export default AttendanceApprovals
